/** Numeric wire values — EPN's backends don't configure JsonStringEnumConverter, so every enum is a
 * plain integer over the wire (see docs/staging-data.md). Label maps below are display-only. */
export const EmploymentStatus = { Active: 0, OnLeave: 1, Terminated: 2 } as const;
export const employmentStatusLabels: Record<number, string> = { 0: 'Active', 1: 'On leave', 2: 'Terminated' };

export const ContractorIdentificationType = { Ssn: 0, Ein: 1 } as const;
export const contractorIdentificationTypeLabels: Record<number, string> = { 0: 'SSN', 1: 'EIN' };

/** A plain preference label only — EPN has no bank account/routing/account-number model anywhere for a
 * contractor (or an employee); see EPN/docs/epn-74-contractor-management-gaps.md. */
export const ContractorPayMethod = { DirectDeposit: 0, Check: 1 } as const;
export const contractorPayMethodLabels: Record<number, string> = { 0: 'Direct Deposit', 1: 'Check' };

export interface EmployeeResponse {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string | null;
  hireDate: string;
  terminationDate: string | null;
  status: number;
  ssnLast4: string | null;
  departmentId: string | null;
  jobTitleId: string | null;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  /** EPN-150. E.164-style with the country code ("+15555550123"). */
  phone?: string | null;
  gender?: string | null;
  /** The SRS "New Hire" toggle; null/absent = not recorded. */
  isNewHire?: boolean | null;
  /** EPN-152 slice 3b: one of `employmentTypeLabels`' numbers; null/absent = not recorded. */
  employmentType?: number | null;
  /** EPN-152. Only the single-employee read fills the primary work location fields (the list leaves them null). */
  workArrangement?: number | null;
  primaryWorkLocationId?: string | null;
  primaryWorkLocationEffectiveFrom?: string | null;
  /** EPN-153. Only the state of the current home address rides on the employee read (never the street). */
  homeAddressState?: string | null;
  /** EPN-188: the current pay method (see `employeePayMethodLabels`); only the single-employee read fills it. */
  payMethod?: number | null;
  /** EPN-232: Employees' verdict that the recorded pay method can pay (a direct deposit needs an approved, payable bank account). Single-employee read only. */
  payMethodReady?: boolean | null;
  /** Job Role Library: the employee LIST fills the role title and internal code (the id alone is not enough to show it, and an
   * Archived role no longer appears in `listJobTitles`). A job role never carries a permission. */
  jobTitleName?: string | null;
  /** When the invited employee submitted their whole onboarding; null until then (and for employees who were never invited). Set only by the employee. */
  onboardingSubmittedAtUtc?: string | null;
  roleCode?: string | null;
  /** Healthcare disciplines: the employee's primary discipline (a classification of the worker, never the work performed). Additive and nullable. */
  disciplineId?: string | null;
  disciplineCode?: string | null;
  disciplineName?: string | null;
}

export interface CreateEmployeeInput {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string | null;
  hireDate: string;
  ssnLast4: string | null;
  departmentId: string | null;
  jobTitleId: string | null;
  phone?: string | null;
  gender?: string | null;
  isNewHire?: boolean | null;
  employmentType?: number | null;
  /** Optional: creates the employee's primary discipline assignment in the same transaction. Omitted when no discipline is chosen. */
  disciplineId?: string | null;
}

export interface UpdateEmployeeInput {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string | null;
  ssnLast4: string | null;
  departmentId: string | null;
  jobTitleId: string | null;
  /** Omitted/null = unchanged (the server never blanks these two). */
  hireDate?: string | null;
  employeeNumber?: string | null;
  /** A null phone/gender/isNewHire/employmentType clears the saved value. */
  phone?: string | null;
  gender?: string | null;
  isNewHire?: boolean | null;
  employmentType?: number | null;
  /** Optional: changes the employee's primary discipline (ends the old primary assignment and appends a new one). Omitted = unchanged. */
  disciplineId?: string | null;
}

/** Mirrors EPN.Employees.Api's `EmployeeSortBy` enum exactly (Name=0, EmployeeNumber=1, HireDate=2) — the
 * gateway sends the string form over the query string, the backend maps it, so no numeric wire value here. */
export type EmployeeSortBy = 'name' | 'employeeNumber' | 'hireDate';

/** The Employee List query — search/filter/sort/pagination are evaluated server-side, scoped to the
 * caller's own active company. Never fetch every employee and filter in the browser. */
export interface ListEmployeesParams {
  search?: string | undefined;
  status?: number | undefined;
  departmentId?: string | undefined;
  jobTitleId?: string | undefined;
  sortBy?: EmployeeSortBy | undefined;
  sortDescending?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface EmployeeListPage {
  items: EmployeeResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Employee Profile Setup's four approved top-level steps (plus EPN-94's read-only Setup Summary) — mirrors CustomPayRunWizard's own
 * WIZARD_STEPS/WizardStepKey pattern (an existing, already-owned record resumed via `:step` in the URL,
 * not local-only wizard state — see EmployeeProfilePage's own comment on why). */
export const EMPLOYEE_PROFILE_STEPS = [
  { key: 'employee-setup', label: 'Employee Setup' },
  { key: 'taxes-exemptions', label: 'Taxes & Exemptions' },
  { key: 'documents', label: 'Upload Documents' },
  { key: 'additional-settings', label: 'Additional Settings' },
  // EPN-94 S1 — read-only, server-computed readiness. It has no fields to save, so the profile page renders
  // no Save/Clear footer for it.
  { key: 'setup-summary', label: 'Setup Summary' },
] as const;

export type EmployeeProfileStepKey = (typeof EMPLOYEE_PROFILE_STEPS)[number]['key'];

export interface ContractorResponse {
  id: string;
  displayName: string;
  businessName: string | null;
  email: string;
  identificationType: number;
  identificationLast4: string | null;
  preferredPayMethod: number | null;
  /** EPN-250: date a W-9 was received (`yyyy-MM-dd`), or null. COMPLIANCE RULE CONFIRMATION REQUIRED: what counts as a completed W-9. */
  w9ReceivedOn: string | null;
  /** EPN-250: the company has set up 1099 reporting for this contractor. */
  form1099Configured: boolean;
  isActive: boolean;
  createdAtUtc: string;
}

export interface CreateContractorInput {
  displayName: string;
  businessName: string | null;
  email: string;
  identificationType: number;
  identificationLast4: string | null;
  preferredPayMethod: number | null;
  /** EPN-250 (optional so older callers keep working; the server treats an omitted value as unchanged on update and empty on create). */
  w9ReceivedOn?: string | null;
  form1099Configured?: boolean;
}

export interface UpdateContractorInput extends CreateContractorInput {
  isActive: boolean;
}

/** Mirrors `ListEmployeesParams`'s own server-side search/filter/pagination shape (EPN-38) — contractors
 * previously filtered/paginated client-side only; EPN-74 closes that gap. */
export interface ListContractorsParams {
  search?: string | undefined;
  isActive?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface ContractorListPage {
  items: ContractorResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ---- EPN-152: work details and work location ----

export interface DepartmentOption {
  id: string;
  name: string;
  isActive: boolean;
  /** Guided fields: the short code of the department (upper case). Null/absent for a department created before codes existed ("No code"). */
  code?: string | null;
  description?: string | null;
}

/** The inline Add Department dialog of the Job Role wizard. It sends `rejectDuplicates: true` (the strict, non get-or-create form):
 * name, code and description are all required and a duplicate name or code is refused with 409. */
export interface CreateDepartmentInput {
  name: string;
  code: string;
  description: string;
}

export interface DepartmentCodeSuggestion {
  suggestion: string;
  available: boolean;
}

export interface JobTitleOption {
  id: string;
  name: string;
  departmentId: string | null;
  isActive: boolean;
  /** Job Role Library additions (`GET /api/job-titles` returns Active roles only). */
  roleCode?: string | null;
  jobFamily?: string | null;
}

/** Identity owns work locations (EPN-37); the profile only needs enough to choose one. */
export interface CompanyWorkLocationOption {
  id: string;
  name: string;
  locationCode: string;
  status: string;
  isDefault: boolean;
}

/** Mirrors EPN.Employees.Api's `EmploymentType` enum exactly. A fixed list on purpose (not add-your-own): informational for now. */
export const employmentTypeLabels: Record<number, string> = { 1: 'Full-time', 2: 'Part-time', 3: 'Seasonal', 4: 'Temporary' };

/** Mirrors EPN.Employees.Api's `WorkArrangement` enum exactly (the SRS defines exactly these four). */
export const workArrangementLabels: Record<number, string> = { 1: 'On-Site', 2: 'Remote', 3: 'Hybrid', 4: 'Traveling' };

export interface WorkLocationAssignmentItem {
  workLocationId: string;
  effectiveFrom: string;
  reason: string | null;
  changedByUserId: string;
  changedAtUtc: string;
  isCurrent: boolean;
}

export interface EmployeeWorkLocationResponse {
  employeeId: string;
  workArrangement: number | null;
  isHomeAddressAlsoWorkLocation: boolean | null;
  primary: WorkLocationAssignmentItem | null;
  /** Newest first; never edited by anyone (the SRS "Work Location History"). */
  history: WorkLocationAssignmentItem[];
  allowedWorkLocationIds: string[];
}

export interface SaveEmployeeWorkLocationInput {
  primaryWorkLocationId: string;
  /** Required when the primary location is new or changes. */
  effectiveDate: string | null;
  reason: string | null;
  workArrangement: number | null;
  isHomeAddressAlsoWorkLocation: boolean | null;
  allowedWorkLocationIds: string[];
}

// ---- EPN-153: home and mailing address ----

export interface AddressValue {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface HomeAddressHistoryItem {
  address: AddressValue;
  effectiveFrom: string;
  changedByUserId: string;
  changedAtUtc: string;
  isCurrent: boolean;
}

export interface EmployeeAddressResponse {
  employeeId: string;
  home: HomeAddressHistoryItem | null;
  /** Newest first; never edited by anyone. */
  homeHistory: HomeAddressHistoryItem[];
  mailing: AddressValue | null;
}

/** What the screen sends. Country is always the United States for now (product owner decision 2026-10-14). */
export interface AddressInput {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string | null;
}

export interface SaveEmployeeAddressInput {
  home: AddressInput;
  /** Required when an existing home address changes; the first address takes the hire date. */
  effectiveDate: string | null;
  /** Null removes the mailing address. */
  mailing: AddressInput | null;
}

/** EPN-216: read-only status for admins. Only the employee can acknowledge; this never carries a user id. */
export interface EmployeeWorkLocationAcknowledgementResponse {
  acknowledgedAtUtc: string | null;
}

/** Two-letter USPS state and territory codes; the server validates the same list. */
export const US_STATE_CODES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS',
  'MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'AS','GU','MP','PR','VI',
] as const;

/** EPN-188: Check is the only pay method offered for now (Direct Deposit waits for reviewed bank-account storage). */
export const employeePayMethodLabels: Record<number, string> = { 0: 'Check', 1: 'Direct Deposit' };

export interface PayMethodHistoryItem {
  method: number;
  effectiveFrom: string;
  changedByUserId: string;
  changedAtUtc: string;
  isCurrent: boolean;
}

export interface EmployeePayMethodResponse {
  employeeId: string;
  current: PayMethodHistoryItem | null;
  /** Newest first; never edited by anyone. */
  history: PayMethodHistoryItem[];
  /** The methods the server will accept right now. The screen offers only these. */
  availableMethods: number[];
}

export interface SaveEmployeePayMethodInput {
  method: number;
  effectiveDate: string | null;
}

/** EPN-193 (ADR 0004 proposal; staging / synthetic data only): the employee's FEDERAL W-4 answers. 0 = 2019 or earlier form,
 * 1 = 2020 or later form. Filing status 0-2 belong to the earlier form, 3-5 to the later one. */
export const w4FormVersionLabels: Record<number, string> = { 0: '2019 or Earlier W-4', 1: '2020 or Later W-4' };
export const federalFilingStatusLabels: Record<number, string> = {
  0: 'Single',
  1: 'Married',
  2: 'Married, but withhold at higher Single rate',
  3: 'Single or Married filing separately',
  4: 'Married filing jointly',
  5: 'Head of household',
};
export const filingStatusesByW4Version: Record<number, number[]> = { 0: [0, 1, 2], 1: [3, 4, 5] };

export interface FederalTaxElectionDetail {
  formVersion: number;
  filingStatus: number;
  isExempt: boolean;
  allowances: number | null;
  extraWithholding: number | null;
  multipleJobs: boolean | null;
  dependentsAmount: number | null;
  otherIncome: number | null;
  deductions: number | null;
  effectiveFrom: string;
  changedByUserId: string;
  changedAtUtc: string;
}

/** History shows when and on which form version, never the answers. */
export interface FederalTaxElectionHistoryItem {
  formVersion: number;
  effectiveFrom: string;
  changedByUserId: string;
  changedAtUtc: string;
  isCurrent: boolean;
}

/** EPN-192: Employees owns only WHICH company pay schedule an employee is on, with effective dates. The schedule's name,
 * frequency, pay day and period start stay in PayrollConfig and are read from there. */
export interface PayScheduleAssignmentHistoryItem {
  payScheduleId: string;
  effectiveFrom: string;
  changedByUserId: string;
  changedAtUtc: string;
  isCurrent: boolean;
}

export interface EmployeePayScheduleResponse {
  employeeId: string;
  current: PayScheduleAssignmentHistoryItem | null;
  /** Newest first; never edited by anyone. */
  history: PayScheduleAssignmentHistoryItem[];
}

export interface SaveEmployeePayScheduleInput {
  payScheduleId: string;
  /** Required by the server when an existing assignment changes; the first one takes the hire date. */
  effectiveDate: string | null;
}

export interface FederalTaxElectionResponse {
  employeeId: string;
  current: FederalTaxElectionDetail | null;
  /** Newest first; never edited by anyone. */
  history: FederalTaxElectionHistoryItem[];
}

export interface SaveFederalTaxElectionInput {
  formVersion: number;
  filingStatus: number;
  isExempt: boolean;
  allowances?: number | null;
  extraWithholding?: number | null;
  multipleJobs?: boolean | null;
  dependentsAmount?: number | null;
  otherIncome?: number | null;
  deductions?: number | null;
  effectiveDate?: string | null;
}

// ---- EPN-216 Step 2: administrator review of an employee's reciprocity certificate (GET/PUT /api/employees/{id}/reciprocity-requests) ----

export type ReciprocityRequestStatusName = 'SubmittedPendingReview' | 'Approved' | 'Rejected' | 'Withdrawn' | 'Revoked';

/** What a request means today: `Expired` is derived on read (approved and past its expiry), never stored. */
export type ReciprocityEffectiveStatus = 'Pending' | 'InEffect' | 'EffectiveInFuture' | 'Expired' | 'Revoked' | 'Rejected' | 'Withdrawn' | 'Stale';

/** One request. No file path and no storage name ever reach the browser. `isStale` = the employee's work or residence state changed since it was submitted. */
export interface ReciprocityRequestAdminItem {
  id: string;
  workState: string;
  residenceState: string;
  certificateForm: string;
  status: ReciprocityRequestStatusName;
  isStale: boolean;
  originalFileName: string;
  sizeBytes: number;
  submittedAtUtc: string;
  reviewedAtUtc: string | null;
  reviewNote: string | null;
  effectiveFrom: string | null;
  /** The approval's last day, when the reviewer recorded one; null = no expiry recorded. Absent on an older server. */
  expiresOn?: string | null;
  /** Approved and past `expiresOn`. */
  isExpired?: boolean;
  revokedAtUtc?: string | null;
  revokeNote?: string | null;
  effectiveStatus?: ReciprocityEffectiveStatus;
}

/** Approve needs an effective date (and may carry an expiry date after it); Reject needs a note. The server enforces both. */
export interface ReviewReciprocityInput {
  decision: 'Approve' | 'Reject';
  effectiveDate?: string;
  /** Approve only: the last day the approval is in effect. Must be after the effective date. */
  expiresOn?: string;
  note?: string;
}

/** POST /api/employees/{id}/reciprocity-requests/{rid}/revoke: only an Approved request, and the note is required. */
export interface RevokeReciprocityInput {
  note: string;
}

// ---- EPN-245: administrator, read-only State Income Tax (Step 2) ----

/** GET /api/employees/{id}/state-tax (employees.read). Built only from data EPN already holds; nothing here is an effective election. */
export interface AdminTaxState {
  code: string | null;
  name: string | null;
  status: 'Known' | 'Unknown';
  source: 'PrimaryWorkLocation' | 'HomeAddressRemote' | 'HomeAddress' | 'LookupUnavailable' | 'NotAssigned' | 'NoHomeAddress';
  note: string;
}

export interface AdminSubmittedStateDraft {
  stateCode: string | null;
  stateName: string | null;
  filingStatus: string | null;
  allowances: number | null;
  mostRecentWithholdingAmount: number | null;
  additionalWithholdingState: string | null;
  additionalWithholdingValue: number | null;
  additionalWithholdingType: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  /** False when the saved state is no longer the work, residence or an allowed additional work state; null when no state was chosen. */
  matchesCurrentStates: boolean | null;
}

export interface AdminStateTaxResponse {
  employeeId: string;
  workState: AdminTaxState;
  residenceState: AdminTaxState;
  workLocation: { primaryWorkLocationId: string | null; lookupUnavailable: boolean };
  multiState: { status: 'NotApplicable' | 'ReviewRequired' | 'Unresolved'; states: string[]; message: string };
  reciprocity: {
    status: 'NotApplicable' | 'Unknown' | 'EligibleCertificateNeeded' | 'SubmittedPendingReview' | 'Approved' | 'Rejected' | 'NeedsReview';
    label: string;
    requiredForm: string | null;
    effectiveFrom: string | null;
    submittedAtUtc: string | null;
  };
  submittedByEmployee: { status: 'NotStarted' | 'SubmittedByEmployeeNotEffective'; notice: string; draft: AdminSubmittedStateDraft | null };
  /** From the controlled reference file, a DRAFT pending payroll/tax specialist approval. Informational only. */
  reference: { version: string; status: string; workStateHasNoWageIncomeTax: boolean | null; residenceStateHasNoWageIncomeTax: boolean | null; note: string };
}

/** One state form field the tax engine describes, vendor-neutral. Nothing is defaulted: `defaultValue` is the engine's own default, metadata only.
 * `allowedValuesText` is the engine's list exactly as received (its separator is not verified, so it is never split). */
export interface StateTaxFormField {
  name: string;
  label: string;
  helpText: string | null;
  dataType: 'Integer' | 'Currency' | 'Boolean' | 'Text' | 'Percentage' | 'Unknown';
  isRequired: boolean | null;
  defaultValue: string | null;
  allowedValuesText: string | null;
  validationPattern: string | null;
  validationPatternDescription: string | null;
  validationKind: string | null;
  formLine: string | null;
}

export interface StateTaxCatalogItem {
  taxItemId: string;
  description: string;
  isEmployerTax: boolean;
  stateCode: string | null;
  isStateIncomeTax: boolean;
}

/** GET /api/tax-calculation/employees/{id}/state-income-tax-fields (employees.read + taxpaymentsettings.read + jurisdictiontaxrules.read; staging sandbox only). */
export interface StateIncomeTaxFieldsResponse {
  employeeId: string;
  effectiveDate: string;
  status: 'Available' | 'NoWorkLocation' | 'NoLocationProfile' | 'NoStateIncomeTaxItem';
  workLocationId: string | null;
  taxItems: StateTaxCatalogItem[];
  stateIncomeTax: { taxItemId: string; description: string; fields: StateTaxFormField[] }[];
  selectionNote: string;
}

/** The stable `code` an error from the fields endpoint carries (ApiError.code). */
export type StateIncomeTaxFieldsErrorCode =
  | 'NotConfigured'
  | 'EnvironmentNotAllowed'
  | 'ProviderUnavailable'
  | 'ProviderRejected'
  | 'UnexpectedResponse'
  | 'DownstreamUnavailable';

/** EPN-216: the per-employee invitation state returned by Identity. Carries no email, name or token. */
export interface EmployeeInvitationState {
  employeeId: string;
  state: 'none' | 'pending' | 'expired' | 'accepted' | 'revoked';
  expiresAtUtc: string | null;
  lastSentAtUtc: string | null;
  lastSendFailed: boolean;
}

/** EPN-216: an employee's question about an employer-owned field, as the administrator sees it. */
export interface EmployeeQuestionItem {
  id: string;
  employeeId: string;
  fieldKey: string;
  message: string;
  createdAtUtc: string;
  status: 'open' | 'answered';
  reply: string | null;
  repliedAtUtc: string | null;
}

// ---- EPN-52 state withholding elections (PR 2 API; administrator screens, PR 4). Answers are shown only to employeetaxelections.read. ----

/** A jurisdiction's certificate status in the controlled catalog (Employees, derived from Taxgin's rule files; a DRAFT pending SME approval). */
export type StateCertificateStatus = 'CertificateRequired' | 'EmptyCertificateAccepted' | 'NoCertificateRequired' | 'NotApplicable' | 'NotEstablished';

export interface StateFilingStatusOption {
  key: string;
  label: string;
}

/** One catalog entry. Every flag comes from Taxgin's engine and rule file; the forms show only what the state takes. */
export interface StateCertificateEntry {
  code: string;
  status: StateCertificateStatus;
  notEstablishedReason: string | null;
  filingStatuses: StateFilingStatusOption[];
  allowsExempt: boolean;
  allowsAdditionalWithholding: boolean;
  allowsExemptions: boolean;
  allowsExemptionAmount: boolean;
  allowsDependents: boolean;
  allowsLowerRateElection: boolean;
  allowsElectedRate: boolean;
  electedRates: number[];
  defaultElectedRate: number | null;
  allowsSchoolDistrictNumber: boolean;
  smeQuestions: string[];
  approvalStatus: string;
}

export interface StateWithholdingCatalogResponse {
  version: string;
  status: string;
  states: StateCertificateEntry[];
}

export interface StateWithholdingAnswers {
  filingStatusKey: string | null;
  exemptions: number | null;
  additionalWithholding: number | null;
  isExempt: boolean;
  electedRate: number | null;
  exemptionAmount: number | null;
  dependents: number | null;
  lowerRateElection: boolean | null;
  schoolDistrictNumber: string | null;
}

export interface StateWithholdingElectionDetail {
  electionId: string;
  stateCode: string;
  effectiveFrom: string;
  sequence: number;
  answers: StateWithholdingAnswers;
  expiresOn: string | null;
  catalogVersion: string;
  submissionId: string;
  approvedByUserId: string;
  approvedAtUtc: string;
}

export type StateWithholdingSubmissionStatus = 'SubmittedPendingReview' | 'Approved' | 'Rejected' | 'Withdrawn';

export interface StateWithholdingSubmissionDetail {
  submissionId: string;
  stateCode: string;
  effectiveFrom: string;
  answers: StateWithholdingAnswers;
  source: 'Admin' | 'Employee';
  status: StateWithholdingSubmissionStatus;
  catalogVersion: string;
  submittedByUserId: string;
  submittedAtUtc: string;
  reviewedByUserId: string | null;
  reviewedAtUtc: string | null;
  electionId: string | null;
  concurrencyVersion: number;
}

/** History shows who and when, never the answers or notes. */
export interface StateWithholdingHistoryItem {
  submissionId: string;
  stateCode: string;
  effectiveFrom: string;
  source: 'Admin' | 'Employee';
  status: StateWithholdingSubmissionStatus;
  submittedByUserId: string;
  submittedAtUtc: string;
  reviewedByUserId: string | null;
  reviewedAtUtc: string | null;
}

export interface StateWithholdingStateView {
  stateCode: string;
  catalogStatus: string;
  current: StateWithholdingElectionDetail | null;
  upcoming: StateWithholdingElectionDetail | null;
  openSubmission: StateWithholdingSubmissionDetail | null;
  history: StateWithholdingHistoryItem[];
}

export interface StateWithholdingElectionsResponse {
  employeeId: string;
  catalogVersion: string;
  states: StateWithholdingStateView[];
}

/** POST body. Only members the state takes are sent; `backdateReason` only when `effectiveFrom` is before today (SME-36 default). */
export interface SubmitStateWithholdingElectionInput {
  stateCode: string;
  effectiveFrom: string;
  filingStatusKey?: string;
  exemptions?: number;
  additionalWithholding?: number;
  isExempt?: boolean;
  electedRate?: number;
  exemptionAmount?: number;
  dependents?: number;
  lowerRateElection?: boolean;
  backdateReason?: string;
}

/** EPN-52 PR 5: one state the signed-in employee can answer for (derived from their work location or home address). */
export interface SelfStateWithholdingStateOption {
  stateCode: string;
  source: 'Work' | 'AdditionalWork' | 'Residence';
  catalogStatus: string;
  acceptsElections: boolean;
}

/** GET /api/me/employee/taxes/state-elections: the employee's own elections, their derived states and the catalog version. */
export interface SelfStateWithholdingElectionsResponse {
  catalogVersion: string;
  derivedStates: SelfStateWithholdingStateOption[];
  states: StateWithholdingStateView[];
}

/** POST /api/me/employee/taxes/state-elections. The employee never backdates (SME-36 default), so there is no backdate reason. */
export type SelfSubmitStateWithholdingElectionInput = Omit<SubmitStateWithholdingElectionInput, 'backdateReason'>;

/** PUT .../review body. A note is required to reject; an expiry only for an exempt election (SME-41 default). */
export interface ReviewStateWithholdingElectionInput {
  decision: 'Approve' | 'Reject';
  note?: string;
  expiresOn?: string;
  concurrencyVersion: number;
}
